import LegalLayout from "@/components/legal/LegalLayout";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function PrivacyPage() {
  useDocumentMeta({
    title: "Privacy Policy",
    description:
      "How Nerd Multiverse collects, uses and protects your personal data, and your rights under UK GDPR.",
    path: "/privacy",
  });

  return (
    <LegalLayout title="Privacy Policy" updated="28 September 2026">
      <section>
        <h2>1. Introduction</h2>
        <p>
          This policy explains what personal data Nerd Multiverse collects
          when you use the marketplace, why we collect it, and the rights
          you have over it. We're the data controller for the personal data
          described here.
        </p>
      </section>

      <section>
        <h2>2. What we collect</h2>
        <ul>
          <li>
            <strong>Account data:</strong> name, email address and role
            (buyer, seller or admin) when you sign up.
          </li>
          <li>
            <strong>Seller & listing data:</strong> your seller application
            status, and the listings you create (title, description, price,
            condition, category and photos).
          </li>
          <li>
            <strong>Order data:</strong> items purchased, order totals and
            status, so buyers and sellers can see their own transaction
            history.
          </li>
          <li>
            <strong>Reviews & favourites:</strong> ratings and comments you
            leave, and items you save as favourites.
          </li>
          <li>
            <strong>Device & usage data:</strong> standard technical data
            collected by our hosting provider (Cloudflare) to serve the
            site, such as IP address and request logs.
          </li>
        </ul>
        <p>
          We don't use third-party advertising trackers or sell your data to
          advertisers.
        </p>
      </section>

      <section>
        <h2>3. How we use your data</h2>
        <ul>
          <li>to create and secure your account, and let you sign in;</li>
          <li>
            to operate the marketplace — showing listings, processing
            orders, and connecting buyers with sellers;
          </li>
          <li>
            to review seller applications and listings before they go live;
          </li>
          <li>
            to notify you of account and order activity (for example, when
            one of your listings sells);
          </li>
          <li>to maintain the security and reliability of the service.</li>
        </ul>
      </section>

      <section>
        <h2>4. Our legal basis</h2>
        <p>
          We process most of your data because it's necessary to perform our
          contract with you (running your account and marketplace
          transactions). Where we have a legitimate interest — such as
          keeping the platform secure — we rely on that instead. We'll ask
          for your consent separately for anything that requires it.
        </p>
      </section>

      <section>
        <h2>5. Who we share data with</h2>
        <p>
          We use the following processors to run Nerd Multiverse:
        </p>
        <ul>
          <li>
            <strong>Firebase (Google Cloud):</strong> authentication,
            database and file storage for account, listing and order data.
          </li>
          <li>
            <strong>Cloudflare:</strong> hosting and content delivery for
            the website itself.
          </li>
        </ul>
        <p>
          We also share the minimum necessary order information between a
          buyer and seller so a transaction can be completed (for example,
          a seller can see who purchased their item so it can be shipped).
          We don't sell your personal data to third parties.
        </p>
      </section>

      <section>
        <h2>6. Cookies and local storage</h2>
        <p>
          Nerd Multiverse uses your browser's local storage to keep your
          shopping cart and sign-in session working between visits, and — if
          you install the app — a service worker to cache pages for
          offline/faster access. We don't use third-party advertising or
          analytics cookies.
        </p>
      </section>

      <section>
        <h2>7. How long we keep your data</h2>
        <p>
          We keep account, listing and order data for as long as your
          account is active, and for a reasonable period afterwards where we
          need it to resolve disputes, keep records required by law, or
          enforce our agreements. You can ask us to delete your account and
          associated data at any time, as described in Section 8.
        </p>
      </section>

      <section>
        <h2>8. Your rights</h2>
        <p>
          Under UK GDPR, you have the right to:
        </p>
        <ul>
          <li>access the personal data we hold about you;</li>
          <li>ask us to correct inaccurate data;</li>
          <li>ask us to delete your data, subject to legal exceptions;</li>
          <li>ask us to restrict or object to certain processing;</li>
          <li>receive your data in a portable format.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us using the details in
          Section 11. If you're unhappy with how we've handled your data,
          you can also complain to the UK Information Commissioner's Office
          (ICO) at{" "}
          <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
            ico.org.uk
          </a>
          .
        </p>
      </section>

      <section>
        <h2>9. Security</h2>
        <p>
          We rely on Firebase's authentication and access-control rules to
          restrict who can read or write your data, and Cloudflare's
          infrastructure to serve the site securely over HTTPS. No system is
          completely secure, but we take reasonable steps to protect your
          information.
        </p>
      </section>

      <section>
        <h2>10. International transfers</h2>
        <p>
          Firebase and Cloudflare may process data on servers outside the
          UK. Where that happens, it's covered by appropriate safeguards
          such as the UK's International Data Transfer Addendum or
          equivalent standard contractual clauses.
        </p>
      </section>

      <section>
        <h2>11. Contact us</h2>
        <p>
          Questions about this policy, or want to exercise your data rights?
          Contact us at{" "}
          <a href="mailto:privacy@nerdmultiverse.co.uk">
            privacy@nerdmultiverse.co.uk
          </a>
          .
        </p>
      </section>

      <section>
        <h2>12. Changes to this policy</h2>
        <p>
          We may update this policy as the marketplace evolves. We'll update
          the "last updated" date above whenever we do.
        </p>
      </section>
    </LegalLayout>
  );
}
